/**
 * 角色卡社区来源注册表（T07b）。
 *
 * 与 `src/core/providers/registry.ts` 同构：注册表只持有「来源实例」，
 * 支持运行时注册 / 覆盖，按 id 取用，并提供默认来源。进程内另有一个默认
 * 注册表（`defaultCommunityRegistry`），构造时即登记内置目录来源，因此
 * `defaultSource()` 开箱即用。
 *
 * 对外便捷函数：`registerSource` / `getSource` / `listSources` / `defaultSource`
 * 都作用在默认注册表上；需要隔离的调用方（例如测试）可以直接 `new`。
 */
import { BUILTIN_SOURCE_ID, createBuiltinSource } from './source';
import type { CommunitySource } from './source';

/** 未登记任何来源时抛出。 */
export class CommunitySourceNotRegisteredError extends Error {
  readonly id: string;

  constructor(id: string) {
    super(`没有登记 id 为 "${id}" 的社区来源`);
    this.name = 'CommunitySourceNotRegisteredError';
    this.id = id;
  }
}

export class CommunitySourceRegistry {
  private readonly sources = new Map<string, CommunitySource>();
  private defaultId: string | null = null;

  /**
   * 登记（或覆盖）一个来源。首个登记的来源自动成为默认来源；
   * 也可以之后用 `setDefault` 显式指定。
   */
  registerSource(source: CommunitySource): this {
    if (typeof source?.list !== 'function' || typeof source?.fetchCard !== 'function') {
      throw new TypeError('社区来源必须实现 list() 与 fetchCard()');
    }
    if (typeof source.id !== 'string' || source.id.trim() === '') {
      throw new TypeError('社区来源缺少非空 id');
    }
    this.sources.set(source.id, source);
    this.defaultId ??= source.id;
    return this;
  }

  unregisterSource(id: string): boolean {
    const removed = this.sources.delete(id);
    if (removed && this.defaultId === id) {
      const [first] = this.sources.keys();
      this.defaultId = first ?? null;
    }
    return removed;
  }

  has(id: string): boolean {
    return this.sources.has(id);
  }

  getSource(id: string): CommunitySource | undefined {
    return this.sources.get(id);
  }

  listSources(): CommunitySource[] {
    return [...this.sources.values()];
  }

  /** 当前默认来源 id（无来源时为 null）。 */
  get defaultSourceId(): string | null {
    return this.defaultId;
  }

  setDefault(id: string): this {
    if (!this.sources.has(id)) throw new CommunitySourceNotRegisteredError(id);
    this.defaultId = id;
    return this;
  }

  /** 取默认来源；没有任何来源时抛 `CommunitySourceNotRegisteredError`。 */
  defaultSource(): CommunitySource {
    const source = this.defaultId === null ? undefined : this.sources.get(this.defaultId);
    if (source === undefined) {
      throw new CommunitySourceNotRegisteredError(this.defaultId ?? '<empty>');
    }
    return source;
  }
}

/**
 * 进程内默认注册表：开箱注册内置目录来源，并把远端来源留给用户按需注册。
 * （注册表只持实例，不会污染内置目录数据。）
 */
export const defaultCommunityRegistry = new CommunitySourceRegistry().registerSource(
  createBuiltinSource(),
);

/** 便捷函数：在默认注册表上登记来源。 */
export function registerSource(source: CommunitySource): CommunitySource {
  defaultCommunityRegistry.registerSource(source);
  return source;
}

/** 便捷函数：按 id 取来源。 */
export function getSource(id: string): CommunitySource | undefined {
  return defaultCommunityRegistry.getSource(id);
}

/** 便捷函数：列出全部来源。 */
export function listSources(): CommunitySource[] {
  return defaultCommunityRegistry.listSources();
}

/** 便捷函数：取默认来源（缺省为内置目录）。 */
export function defaultSource(): CommunitySource {
  return defaultCommunityRegistry.defaultSource();
}

export { BUILTIN_SOURCE_ID };